// Typ: ParallelogramAreaFromSidesAndAngleTask
// Wzór: P = a * b * sin(α)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview
} from "../../../../components/inputs/RootFractionInput";

// utils
const rnd = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

// sin wartości „ładnych” kątów (symbolicznie i numerycznie)
const SIN_INFO = {
  30:  { num: 1, den: 2, root: null, latex: "\\tfrac{1}{2}" },
  45:  { num: 1, den: 2, root: 2,    latex: "\\tfrac{\\sqrt{2}}{2}" },
  60:  { num: 1, den: 2, root: 3,    latex: "\\tfrac{\\sqrt{3}}{2}" },
  120: { num: 1, den: 2, root: 3,    latex: "\\tfrac{\\sqrt{3}}{2}" }, // = sin 60
  135: { num: 1, den: 2, root: 2,    latex: "\\tfrac{\\sqrt{2}}{2}" }, // = sin 45
  150: { num: 1, den: 2, root: null, latex: "\\tfrac{1}{2}" },         // = sin 30
};

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// format (k/den)*√root jako LaTeX
function formatCoeffWithRoot(k, den, root) {
  const d = gcd(Math.abs(k), Math.abs(den));
  let num = k / d;
  let denS = den / d;

  if (!root) {
    if (denS === 1) return `${num}`;
    return `\\dfrac{${num}}{${denS}}`;
  }

  if (num === 1 && denS === 1) return `\\sqrt{${root}}`;
  if (denS === 1) return `${num}\\sqrt{${root}}`;
  if (num === 1) return `\\dfrac{\\sqrt{${root}}}{${denS}}`;
  return `\\dfrac{${num}\\sqrt{${root}}}{${denS}}`;
}

// === generator ===
export function generateTask() {
  const A = Array.from({ length: 15 }, (_, i) => i + 3); // 3..17
  const a = rnd(3, 15);
  const b = rnd(3, 15);
  const angles = [30, 45, 60, 120, 135, 150];
  const alpha = angles[rnd(0, angles.length - 1)];
  return { type: "ParallelogramAreaFromSidesAndAngleTask", data: { a, b, alpha } };
}

// === render task ===
export function renderTask({ data }) {
  const { a, b, alpha } = data;
  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      Dany jest równoległobok o bokach <InlineMath math={`a=${a}`} /> i <InlineMath math={`b=${b}`} /> oraz kącie
      między nimi <InlineMath math={`\\alpha=${alpha}^\\circ`} />.
      Oblicz jego pole <InlineMath math={"P"} />.
    </div>
  );
}

// === input ===
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="P"
      placeholder="np. (13√3)/4, √2/8, 15/2"
    />
  );
}

// === check ===
export function checkAnswer({ data }, userAnswer) {
  const { a, b, alpha } = data;
  const s = SIN_INFO[alpha];
  if (!s) return false;

  const expected =
    a * b * (s.root ? Math.sqrt(s.root) / s.den : s.num / s.den);

  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === "string") {
    const asNumber = Number(raw.replace(",", "."));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;
  return Math.abs(parsed - expected) <= 0.01;
}

// === answer ===
export function renderAnswer({ data }) {
  const { a, b, alpha } = data;
  const s = SIN_INFO[alpha];
  const k = a * b * (s.num ?? 1);
  const den = s.den ?? 1;
  const latexCoeff = formatCoeffWithRoot(k, den, s.root);

  return <InlineMath math={`P=${latexCoeff}`} />;
}

// === explanation ===
export function renderExplanation({ data }) {
  const { a, b, alpha } = data;
  const s = SIN_INFO[alpha];

  const prod = a * b;
  const baseLatex = s.root
    ? `\\dfrac{\\sqrt{${s.root}}}{${s.den}}`
    : `\\dfrac{${s.num}}{${s.den}}`;

  const numer0 = prod * (s.num ?? 1);
  const denom0 = s.den ?? 1;
  const unsimplified = s.root
    ? `\\dfrac{${prod}\\,\\sqrt{${s.root}}}{${denom0}}`
    : `\\dfrac{${prod}}{${denom0}}`;

  const simplified = formatCoeffWithRoot(numer0, denom0, s.root);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <strong>Krok 1.</strong> Wzór na pole równoległoboku: <InlineMath math={"P=a\\,b\\,\\sin\\alpha"} />.
      <BlockMath math={`P=${a}\\cdot ${b}\\cdot \\sin ${alpha}^\\circ`} />
      <BlockMath math={`\\sin ${alpha}^\\circ = ${s.latex}`} />
      <BlockMath math={`P=${a}\\cdot ${b}\\cdot ${baseLatex}`} />
      <BlockMath math={`P=${unsimplified}`} />
      <BlockMath math={`P=${simplified}`} />
    </div>
  );
}

// === user answer ===
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(raw, "P")} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
